import { FirebaseUserModel, UserSettings } from '../../../../types';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { storeFirebaseUser, UserKeys } from '../../../api/users-api';
import { useStore } from '../../state/zustand-store';
import { SubmitHandler, UseFormHandleSubmit } from 'react-hook-form';
import { DriveSearchForm } from './drive-search-page';
import { useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction } from 'react';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../shared/analytics/module-list';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import CancelButton from '../../../shared/buttons/cancel-button';
import SearchButton from '../../../shared/buttons/search-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';

import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';

interface DriveSearchGridToolbarProps {
  driveRoles: string[];
  handleExportMutate: () => void;
  handleSearch: (data: Partial<DriveSearchForm>) => void;
  isExporting: boolean;
  isSearchedDocsEmpty: boolean;
  handleSubmit: UseFormHandleSubmit<
    Partial<DriveSearchForm>,
    Partial<DriveSearchForm>
  >;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  numberOfSearchedUsers: number;
  erroredUsers: string[];
}

export default function DriveSearchGridToolbar({
  driveRoles,
  handleExportMutate,
  handleSearch,
  isExporting,
  isSearchedDocsEmpty,
  handleSubmit,
  isLoading,
  setIsLoading,
  numberOfSearchedUsers,
  erroredUsers,
}: DriveSearchGridToolbarProps) {
  const { firebaseUser, setFirebaseUser } = useStore();
  const queryClient = useQueryClient();
  const onSubmit: SubmitHandler<Partial<DriveSearchForm>> = (data) => {
    handleSearch(data);
  };

  return (
    <>
      <SharedToolbar
        primaryButtons={
          isLoading ? (
            <CancelButton
              onClick={async () => {
                setIsLoading(false);
                const oldId = firebaseUser.firebaseDbPath.split('/');
                const newId = `${oldId[0]}/${oldId[1]}/${Date.now()}`;

                const newUser: FirebaseUserModel = {
                  ...firebaseUser,
                  firebaseDbPath: newId,
                  lastKeepAliveMessageSent: Date.now(),
                  alive: Date.now(),
                };

                // channel gets set and switched
                await storeFirebaseUser(newUser);
                setFirebaseUser(newUser);

                queryClient.setQueryData<UserSettings>(
                  [UserKeys.UserSettings],
                  (old) => {
                    if (!old) return old;

                    return {
                      ...old,
                      firebaseDbPath: newUser.firebaseDbPath,
                    };
                  }
                );
              }}
            />
          ) : (
            <SearchButton onClick={handleSubmit(onSubmit)} />
          )
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="export"
              onClick={() => handleExportMutate()}
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanExportDriveSearch.value
                ) ||
                isLoading ||
                isSearchedDocsEmpty
              }
              isLoading={isExporting}
              tooltipLabel="Export Results"
            />
            <SharedGridStateIconButton
              gridStateKey="driveSearchesGridStates"
              module={ModuleList.modules.drive.name}
              section={ModuleList.modules.drive.sections.driveSearch}
            />
          </>
        }
      />
      {(numberOfSearchedUsers > 0 || erroredUsers.length > 0) && (
        <Box
          sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          {numberOfSearchedUsers > 0 && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {`Searched ${numberOfSearchedUsers} user(s)`}
            </Typography>
          )}
          {erroredUsers.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem />
              <Tooltip
                arrow
                title={`These users are likely suspended, archived or label has restricted permissions: 
            ${erroredUsers.join(', ')}
              `}
              >
                <Typography
                  sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                >
                  {`${erroredUsers.length} errored user(s)`}
                </Typography>
              </Tooltip>
            </>
          )}
        </Box>
      )}
      <Box sx={{ width: '100%', height: 4 }}>
        {isLoading ? (
          <LinearProgress
            variant="indeterminate"
            sx={{ width: '100%', height: 4 }}
          />
        ) : null}
      </Box>
    </>
  );
}
